/** 登录视图；二次验证、绑手机是内部步骤，不写入 URL。 */
import type { LoginMethod } from '../../utils/loginMethodCatalog';
export type { LoginMethod } from '../../utils/loginMethodCatalog';
export const PHONE_PATTERN = /^1[3-9]\d{9}$/;

/** 登录仅开放大陆与香港区号；香港短信号码使用八位本地号码。 */
export type LoginPhoneRegion = 'cn' | 'hk';
export const LOGIN_PHONE_CODES: Record<LoginPhoneRegion, string> = {
  cn: '+86',
  hk: '+852',
};

/** 清洗号码并识别粘贴的国际区号；不截断超长号码，避免向被截断后的另一号码发短信。 */
export function parseLoginPhone(
  value: string,
  region: LoginPhoneRegion = 'cn',
): { region: LoginPhoneRegion; local: string } {
  let cleaned = value.trim().replace(/[\s()-]/g, '');
  for (const [candidate, prefixes] of [
    ['hk', ['+852', '00852']],
    ['cn', ['+86', '0086']],
  ] as const) {
    const prefix = prefixes.find((item) => cleaned.startsWith(item));
    if (prefix)
      return {
        region: candidate,
        local: cleaned.slice(prefix.length).replace(/\D/g, ''),
      };
  }
  if (/^852[2-9]\d{7}$/.test(cleaned))
    return { region: 'hk', local: cleaned.slice(3) };
  if (/^861[3-9]\d{9}$/.test(cleaned))
    return { region: 'cn', local: cleaned.slice(2) };
  // 未开放的国际区号必须保持非法，不能剥掉 + 后误识别为大陆身份。
  if (cleaned.startsWith('+') || cleaned.startsWith('00'))
    return { region, local: cleaned };
  cleaned = cleaned.replace(/\D/g, '');
  return { region, local: cleaned };
}

/** 保留原清洗方法供已有调用使用；区号选择控件使用 parseLoginPhone 的完整结果。 */
export function normalizeLoginPhone(value: string): string {
  return parseLoginPhone(value).local;
}

/** 根据区号显示大陆 3-4-4 或香港 4-4 分组，不修改真实提交值。 */
export function formatLoginPhone(
  value: string,
  region: LoginPhoneRegion = 'cn',
): string {
  return (
    region === 'hk'
      ? [value.slice(0, 4), value.slice(4)]
      : [value.slice(0, 3), value.slice(3, 7), value.slice(7)]
  )
    .filter(Boolean)
    .join(' ');
}

/** 与 Platform 支持的香港八位号码范围保持一致，固话能否接收短信由通道决定。 */
export function isLoginPhoneValid(
  value: string,
  region: LoginPhoneRegion = 'cn',
): boolean {
  return region === 'hk'
    ? /^[2-9]\d{7}$/.test(value)
    : PHONE_PATTERN.test(value);
}

/** 统一以 E.164 传递身份，香港号码不会被当作大陆号码查询或发送。 */
export function loginPhoneE164(
  value: string,
  region: LoginPhoneRegion,
): string {
  return LOGIN_PHONE_CODES[region] + value;
}

/** 未指定或非法 method 保持原有手机号视图；App 扫码沿用同一切换入口。 */
export function readLoginMethod(search: string): LoginMethod {
  const value = new URLSearchParams(search).get('method');
  return value === 'appqr' || value === 'password' || value === 'wechat' ? value : 'phone';
}

/** 只接受同源的根相对跳转，拒绝协议相对地址、反斜杠和控制字符。 */
export function safeLoginRedirect(search: string): string | null {
  const value = new URLSearchParams(search).get('redirect');
  if (
    !value?.startsWith('/') ||
    value.startsWith('//') ||
    /[\\\u0000-\u001f\u007f]/.test(value)
  )
    return null;
  const parsed = new URL(value, 'https://login.invalid');
  return parsed.origin === 'https://login.invalid'
    ? `${parsed.pathname}${parsed.search}${parsed.hash}`
    : null;
}

/** 倒计时以绝对时间计算，后台节流、刷新或休眠不会延长等待。 */
export function remainingSmsSeconds(
  deadline: number,
  now = Date.now(),
): number {
  if (!Number.isFinite(deadline) || deadline - now > 60_000) return 0;
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

/** 只暴露枚举和计数的本地埋点扩展口；不记录号码、凭据、二维码或 URL 查询参数。 */
export function emitLoginEvent(
  name: string,
  detail: Record<string, string | number | boolean>,
): void {
  window.dispatchEvent(
    new CustomEvent('bomate:login', { detail: { name, ...detail } }),
  );
}

/** 兼容设计错误码与 Platform 现有小写错误码；未知错误保留服务端消息。 */
export function loginErrorKey(code: string): string | null {
  const keys: Record<string, string> = {
    PHONE_INVALID: 'authInvalidPhone',
    INVALID_PHONE: 'authInvalidPhone',
    SMS_TOO_FREQUENT: 'loginSmsFrequent',
    SMS_DAILY_LIMIT: 'loginSmsDailyLimit',
    CODE_INVALID: 'loginSmsInvalid',
    INVALID_CODE: 'loginSmsInvalidOrExpired',
    CODE_EXPIRED: 'loginSmsExpired',
    PASSWORD_INVALID: 'loginPasswordInvalid',
    AGREEMENT_REQUIRED: 'authPleaseAgree',
  };
  // ACCOUNT_LOCKED 的具体剩余时间只能由服务端给出，不替换成缺少时间的模板。
  return keys[code.toUpperCase()] ?? null;
}
